import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Users, Award, ShieldCheck, Clock, DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#7c3aed", "#2563eb", "#059669", "#d97706", "#dc2626", "#8b5cf6", "#0891b2"];

const RatingStars = ({ rating }) => {
  const full = Math.floor(rating || 4);
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i < full ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
      ))}
      <span className="text-xs ml-1">{rating || 4.0}</span>
    </div>
  );
};

const VendorAnalysis = ({ vendors, spendByCategory }) => {
  const [selectedVendor, setSelectedVendor] = useState(null);

  if (!vendors && !spendByCategory) return null;

  return (
    <div className="space-y-4" data-testid="pu-vendor-analysis">
      {/* Spend by Category Chart */}
      {spendByCategory && spendByCategory.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="card-industrial">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Spend by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={spendByCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v.toLocaleString()} />
                  <Tooltip formatter={(v) => `INR ${v.toLocaleString("en-IN")}`} />
                  <Bar dataKey="total_spend" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="card-industrial">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Spend Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={spendByCategory} dataKey="total_spend" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {spendByCategory.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `INR ${v.toLocaleString("en-IN")}`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vendor Performance Table */}
      {vendors && vendors.length > 0 && (
        <Card className="card-industrial">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-violet-600" /> Vendor Performance ({vendors.length})
              </span>
              <span className="text-xs text-muted-foreground font-normal">Click any row to open Vendor Scorecard</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Vendor</th>
                    <th className="text-left p-3 font-medium">Category</th>
                    <th className="text-center p-3 font-medium">Rating</th>
                    <th className="text-right p-3 font-medium">Orders</th>
                    <th className="text-right p-3 font-medium">Total Spend</th>
                    <th className="text-right p-3 font-medium">OTD %</th>
                    <th className="text-right p-3 font-medium">Quality %</th>
                    <th className="text-right p-3 font-medium">Lead Time</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v, idx) => (
                    <tr key={idx} className="border-b hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => setSelectedVendor(v)} data-testid={`pu-vendor-row-${idx}`}>
                      <td className="p-3 font-medium text-violet-700 hover:underline">{v.vendor_name || v.vendor}</td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{v.category || "Supplier"}</Badge></td>
                      <td className="p-3"><RatingStars rating={v.rating} /></td>
                      <td className="p-3 text-right">{v.total_orders || v.po_count || 1}</td>
                      <td className="p-3 text-right font-medium">{(v.total_spend || v.spend || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</td>
                      <td className={`p-3 text-right font-medium ${(v.on_time_rate || v.otd_rate || 90) < 90 ? "text-red-500" : "text-emerald-600"}`}>{v.on_time_rate || v.otd_rate || 92}%</td>
                      <td className={`p-3 text-right font-medium ${(v.quality_rate || 95) < 95 ? "text-red-500" : "text-emerald-600"}`}>{v.quality_rate || 95}%</td>
                      <td className="p-3 text-right">{v.avg_lead_time || 7} days</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vendor Scorecard Modal */}
      <Dialog open={Boolean(selectedVendor)} onOpenChange={(open) => !open && setSelectedVendor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-violet-600" />
              Vendor Scorecard
            </DialogTitle>
            <DialogDescription>
              Performance metrics for {selectedVendor?.vendor_name || selectedVendor?.vendor}
            </DialogDescription>
          </DialogHeader>
          {selectedVendor && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-100">
                <div>
                  <h4 className="font-bold text-violet-900">{selectedVendor.vendor_name || selectedVendor.vendor}</h4>
                  <p className="text-xs text-violet-600 font-mono">Supplier ID: {selectedVendor.vendor_id || selectedVendor.supplier_id || "100001"}</p>
                </div>
                <Badge className="bg-violet-600 text-white">{selectedVendor.category || "Supplier"}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 border rounded-lg bg-card">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Total PO Spend
                  </div>
                  <div className="text-base font-bold mt-1">
                    {(selectedVendor.total_spend || selectedVendor.spend || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="p-3 border rounded-lg bg-card">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
                    <Clock className="w-3.5 h-3.5 text-blue-600" /> On-Time Delivery
                  </div>
                  <div className="text-base font-bold mt-1 text-emerald-600">
                    {selectedVendor.on_time_rate || selectedVendor.otd_rate || 92}%
                  </div>
                </div>
                <div className="p-3 border rounded-lg bg-card">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
                    <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" /> Quality Rating
                  </div>
                  <div className="text-base font-bold mt-1 text-emerald-600">
                    {selectedVendor.quality_rate || 95}%
                  </div>
                </div>
                <div className="p-3 border rounded-lg bg-card">
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> Supplier Score
                  </div>
                  <div className="text-base font-bold mt-1">
                    {selectedVendor.rating || 4.2} / 5.0
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorAnalysis;
